import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";

const MENS_SETS = [
  { id: "mens-set-1", label: "Set 01 — All Black", items: [["sync-pulse-tee", "Black"], ["sync-tempo-shorts", "Black"], ["sync-pulse-hat", "Black"]] },
  { id: "mens-set-2", label: "Set 02 — Grey × Black", items: [["sync-motion-tee", "Dark Grey"], ["sync-core-shorts", "Black"]] },
];
const WOMENS_SETS = [
  { id: "womens-set-1", label: "Set 01 — All Black", items: [["sync-flow-v-neck-bra", "Black"], ["sync-pulse-shorts-womens", "Black"]] },
  { id: "womens-set-2", label: "Set 02 — Soft Pink", items: [["sync-pulse-color-block-bra", "Light Pink"], ["sync-flow-flare-pants", "Light Pink"]] },
  { id: "womens-set-3", label: "Set 03 — Court Blues", items: [["sync-core-polo-tank", "Blue with White"], ["sync-core-skirt-womens", "Sky Blue"]] },
  { id: "womens-set-4", label: "Set 04 — All White", items: [["sync-aero-crop-tee", "White"], ["sync-pulse-pocket-shorts", "White"]] },
];

function SetCard({ set, products }) {
  const items = set.items.map(([id, color]) => ({ p: products[id], color })).filter((i) => i.p);
  if (items.length === 0) return null;
  return (
    <div className="border border-border p-6 sm:p-8" data-testid={set.id}>
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-chalk">{set.label}</p>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite">{items.length} pieces</span>
      </div>
      <div className={`grid gap-4 ${items.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {items.map(({ p, color }) => (
          <Link key={p.id} to={`/product/${p.id}`} className="group block" data-testid={`set-item-${p.id}`}>
            <div className="bg-white overflow-hidden aspect-square">
              <img src={p.images_by_color?.[color]?.[0] || p.images[0]} alt={p.name}
                className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
            </div>
            <p className="text-sm mt-3 leading-tight group-hover:text-signal transition-colors">{p.name}</p>
            <p className="font-mono text-[11px] text-graphite uppercase tracking-wider mt-1">{color} · ${p.price.toFixed(2)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function OutfitSets() {
  const [products, setProducts] = useState({});
  const [pct, setPct] = useState(null);
  useEffect(() => {
    api.get("/products").then((r) => {
      const m = {};
      r.data.forEach((p) => { m[p.id] = p; });
      setProducts(m);
    }).catch(() => {});
    api.get("/site/status").then((r) => setPct(r.data.look_discount_percent ?? 10)).catch(() => setPct(10));
  }, []);

  return (
    <section className="bg-ink pb-28" data-testid="outfit-sets">
      <div className="container-sync">
        <p className="eyebrow mb-4">Complete looks</p>
        <h2 className="font-anton uppercase text-4xl sm:text-6xl leading-none">SYNC Sets</h2>
        <p className="mt-4 text-graphite max-w-xl">Head-to-toe outfits built from the collection — grab a full set, or tap any piece to shop it on its own.</p>
        {pct > 0 && (
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-signal mt-3" data-testid="sets-discount-note">
            Buy a full set (top + bottom) — {pct}% off applied automatically at checkout
          </p>
        )}

        <p className="font-mono text-xs uppercase tracking-[0.3em] text-signal mt-12 mb-6" data-testid="mens-sets-label">Men's Sets</p>
        <div className="grid md:grid-cols-2 gap-6">
          {MENS_SETS.map((s) => <SetCard key={s.id} set={s} products={products} />)}
        </div>

        <p className="font-mono text-xs uppercase tracking-[0.3em] text-signal mt-14 mb-6" data-testid="womens-sets-label">Women's Sets</p>
        <div className="grid md:grid-cols-2 gap-6">
          {WOMENS_SETS.map((s) => <SetCard key={s.id} set={s} products={products} />)}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-16">
          <Link to="/shop?gender=men" data-testid="shop-mens-banner"
            className="group border border-border p-8 sm:p-10 flex items-end justify-between hover:border-chalk transition-colors">
            <div>
              <p className="eyebrow mb-3">The full lineup</p>
              <h3 className="font-anton uppercase text-4xl sm:text-5xl">Men's<br />Collection</h3>
            </div>
            <ArrowRight size={28} className="text-graphite group-hover:text-signal group-hover:translate-x-1 transition-all" />
          </Link>
          <Link to="/shop?gender=women" data-testid="shop-womens-banner"
            className="group border border-border p-8 sm:p-10 flex items-end justify-between hover:border-chalk transition-colors">
            <div>
              <p className="eyebrow mb-3">New — just landed</p>
              <h3 className="font-anton uppercase text-4xl sm:text-5xl">Women's<br />Collection</h3>
            </div>
            <ArrowRight size={28} className="text-graphite group-hover:text-signal group-hover:translate-x-1 transition-all" />
          </Link>
        </div>
      </div>
    </section>
  );
}
